"use client"

import type React from "react"
import { observer } from "mobx-react-lite"
import { useState } from "react"
import Rating, { BarRating } from "../../shared/components/rating/Rating"
import {
  totalQ2CompanyObjectiveRating,
  totalQ2MeasureCompanyRating,
  totalQ4MeasureCompanyRating,
} from "../../shared/functions/Scorecard"
import { NoData } from "./NoData"
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react" // Icons for measure items
import type { IMeasureCompany } from "../../../shared/models/MeasureCompany"
import type { IObjectiveCompany } from "../../../shared/models/ObjectiveCompany"
import { useAppContext } from "../../../shared/functions/Context"
import ErrorBoundary from "../../../shared/components/error-boundary/ErrorBoundary"
import { Button } from "./ui/button"
import { cn } from "../../shared/utils/utils"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"

interface MeasureItemProps {
  measure: IMeasureCompany
}

export const MeasureItem = (props: MeasureItemProps) => {
  const { measure } = props
  const rating = measure.q2FinalRating || 0
  let icon: React.ReactNode
  let textColorClass: string

  if (rating >= 3) {
    icon = <CheckCircle2 className="w-5 h-5 text-green-500" />
    textColorClass = "text-green-700"
  } else if (rating >= 2) {
    icon = <AlertTriangle className="w-5 h-5 text-yellow-500" />
    textColorClass = "text-yellow-700"
  } else {
    icon = <XCircle className="w-5 h-5 text-red-500" />
    textColorClass = "text-red-700"
  }

  return (
    <li className="py-3 px-4 flex items-center justify-between border-b last:border-b-0">
      <div className="flex items-center gap-3">
        {icon}
        <div className={cn("text-sm font-medium", textColorClass)}>{measure.description}</div>
      </div>
      <Rating rate={rating} isUpdated={measure.isUpdated} />
    </li>
  )
}

interface ObjectiveItemProps {
  objective: IObjectiveCompany
}

const ObjectiveList = observer((props: ObjectiveItemProps) => {
  const { store } = useAppContext()
  const { objective } = props

  const getMeasures = (obj: IObjectiveCompany): IMeasureCompany[] => {
    return store.companyMeasure.all
      .filter((measure) => measure.asJson.objective === obj.id)
      .map((measure) => measure.asJson)
  }

  const calculateRating = () => {
    const measures = getMeasures(objective)
    const measuresUpdated = measures.filter((measure) => measure.isUpdated).length > 0
    const rating = totalQ2CompanyObjectiveRating(measures)
    return {
      rate: rating,
      isUpdated: measuresUpdated,
    }
  }

  return (
    <li className="py-3 px-4 flex items-center justify-between border-b last:border-b-0">
      <div className="flex items-center gap-3">
        <div className="text-sm font-medium text-gray-800">{objective.description}</div>
      </div>
      <Rating rate={calculateRating().rate} isUpdated={calculateRating().isUpdated} />
    </li>
  )
})

const ObjectivesCard = observer((props: ObjectiveItemProps) => {
  const { store } = useAppContext()
  const { objective } = props

  const getMeasures = (obj: IObjectiveCompany): IMeasureCompany[] => {
    return store.companyMeasure.all
      .filter((measure) => measure.asJson.objective === obj.id)
      .map((measure) => measure.asJson)
  }

  const calculateRating = () => {
    const measures = getMeasures(objective)
    const measuresUpdated = measures.filter((measure) => measure.isUpdated).length > 0
    const rating = totalQ2CompanyObjectiveRating(measures)
    return {
      rate: rating,
      isUpdated: measuresUpdated,
    }
  }

  return (
    <Card className="h-full flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-gray-800 leading-tight">{objective.description}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ErrorBoundary>
          <div className="grid grid-cols-2 gap-4 items-center">
            <div>
              <BarRating rating={calculateRating().rate} />
            </div>
            <div className="flex justify-end">
              <Rating rate={calculateRating().rate} isUpdated={calculateRating().isUpdated} />
            </div>
          </div>
        </ErrorBoundary>
      </CardContent>
    </Card>
  )
})

const ObjectivesTable = observer(() => {
  const { store } = useAppContext()
  const objectives = store.companyObjective.all

  return (
    <ul className="divide-y divide-gray-200 bg-white rounded-lg shadow-sm">
      {objectives.length === 0 ? (
        <NoData message="No objectives to display. Please add a new objective." />
      ) : (
        objectives.map((objective) => (
          <ErrorBoundary key={objective.asJson.id}>
            <ObjectiveList objective={objective.asJson} />
          </ErrorBoundary>
        ))
      )}
    </ul>
  )
})

const ObjectivesGrid = observer(() => {
  const { store } = useAppContext()
  const objectives = store.companyObjective.all

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {objectives.length === 0 ? (
        <div className="col-span-full">
          <NoData message="No objectives to display." />
        </div>
      ) : (
        objectives.map((objective) => (
          <ErrorBoundary key={objective.asJson.id}>
            <ObjectivesCard objective={objective.asJson} />
          </ErrorBoundary>
        ))
      )}
    </div>
  )
})

const MetricsAnalytics = observer(() => {
  const { store } = useAppContext()
  const objectives = store.companyObjective.all
  const measures = store.companyMeasure.all
  const redMeasures = measures.filter((measure) => (measure.asJson.q2FinalRating || 0) <= 2)
  const $measures = measures.map((measure) => measure.asJson)
  const midtermRating = totalQ2MeasureCompanyRating($measures)
  const finalRating = totalQ4MeasureCompanyRating($measures) // Assuming Q4 is used for final here based on original code

  const StatCard = ({
    value,
    label,
    icon,
    colorClass,
  }: { value: string | number; label: string; icon: React.ReactNode; colorClass: string }) => (
    <Card className="flex items-center p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className={cn("p-3 rounded-full mr-4", colorClass)}>{icon}</div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-600">{label}</p>
      </div>
    </Card>
  )

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
      <StatCard
        value={measures.length}
        label="All KPIs"
        icon={<CheckCircle2 className="w-6 h-6 text-white" />}
        colorClass="bg-blue-500"
      />
      <StatCard
        value={redMeasures.length}
        label="Red KPIs"
        icon={<XCircle className="w-6 h-6 text-white" />}
        colorClass="bg-red-500"
      />
      <StatCard
        value={objectives.length}
        label="Objectives"
        icon={<CheckCircle2 className="w-6 h-6 text-white" />}
        colorClass="bg-green-500"
      />
      <StatCard
        value={midtermRating.toFixed(2)}
        label="Midterm Score"
        icon={<CheckCircle2 className="w-6 h-6 text-white" />}
        colorClass="bg-purple-500"
      />
      <StatCard
        value={finalRating.toFixed(2)}
        label="Final Score"
        icon={<CheckCircle2 className="w-6 h-6 text-white" />}
        colorClass="bg-indigo-500"
      />
    </div>
  )
})

const ObjectivesAnalytics = () => {
  const [viewType, setViewType] = useState<"table" | "grid">("grid")

  return (
    <Card className="mb-6 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-semibold text-gray-900">Objectives</CardTitle>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setViewType("grid")}
            className={cn(viewType === "grid" ? "bg-blue-500 text-white" : "text-gray-600 hover:bg-gray-100")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-layout-grid"
            >
              <rect width="7" height="7" x="3" y="3" rx="1" />
              <rect width="7" height="7" x="14" y="3" rx="1" />
              <rect width="7" height="7" x="14" y="14" rx="1" />
              <rect width="7" height="7" x="3" y="14" rx="1" />
            </svg>
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setViewType("table")}
            className={cn(viewType === "table" ? "bg-blue-500 text-white" : "text-gray-600 hover:bg-gray-100")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-table"
            >
              <path d="M12 2v20" />
              <path d="M2 7h20" />
              <path d="M2 17h20" />
              <rect width="18" height="18" x="3" y="3" rx="2" />
            </svg>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {viewType === "table" && <ObjectivesTable />}
        {viewType === "grid" && <ObjectivesGrid />}
      </CardContent>
    </Card>
  )
}

const MeasuresAnalytics = observer(() => {
  const { store } = useAppContext()
  const measures = store.companyMeasure.all.map((m) => m.asJson)
  const redMeasures = measures.filter((measure) => (measure.q2FinalRating || 0) < 2)
  const greenMeasures = measures.filter((measure) => (measure.q2FinalRating || 0) >= 3)
  const amberMeasures = measures.filter(
    (measure) => (measure.q2FinalRating || 0) < 3 && (measure.q2FinalRating || 0) >= 2,
  )

  const MeasureListCard = ({
    title,
    measures,
    message,
    icon,
  }: { title: string; measures: IMeasureCompany[]; message: string; icon: React.ReactNode }) => (
    <Card className="h-full shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          {icon} {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y divide-gray-200 bg-white rounded-b-lg">
          {measures.length === 0 ? (
            <NoData message={message} />
          ) : (
            measures.map((measure) => (
              <ErrorBoundary key={measure.id}>
                <MeasureItem measure={measure} />
              </ErrorBoundary>
            ))
          )}
        </ul>
      </CardContent>
    </Card>
  )

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
      <MeasureListCard
        title="Green KPIs/Measures"
        measures={greenMeasures}
        message="No data to display."
        icon={<CheckCircle2 className="w-5 h-5 text-green-500" />}
      />
      <MeasureListCard
        title="Amber KPIs/Measures"
        measures={amberMeasures}
        message="No data to display."
        icon={<AlertTriangle className="w-5 h-5 text-yellow-500" />}
      />
      <MeasureListCard
        title="All Red KPIs/Measures"
        measures={redMeasures}
        message="No red measures."
        icon={<XCircle className="w-5 h-5 text-red-500" />}
      />
    </div>
  )
})

export const CompanyDashboardQ2 = observer(() => {
  return (
    <ErrorBoundary>
      <div>
        <MetricsAnalytics />
        <ObjectivesAnalytics />
        <MeasuresAnalytics />
      </div>
    </ErrorBoundary>
  )
})
